import PerformanceChart from 'components/GradingInsights/PerformanceChart';
import { useTranslation } from 'react-i18next';
import { Box, Button, Paper, Stack, styled, Text } from 'shell/components';
import { useDraggableScroll } from 'shell/hooks';
import { FilterOption } from 'utils/constants';

const ClassPerformance = ({
    classPerformanceStats: stats,
    classPerformanceChartData: chartData,
    performanceFilters: filters,
    setPerformanceFilters: setFilters,
    filterOptions,
    device,
    onGraderFilterChange,
}) => {
    const { t } = useTranslation('assignment');
    const chartTitle =
        filters.criterion === FilterOption.ALL
            ? t('submission.overall-scores-label')
            : filterOptions.criteria.find((criterion) => criterion.id === filters.criterion)?.title ||
              t('submission.criterion-scores-label');

    return (
        <Container spacing={5}>
            <Stack spacing={3}>
                <Text variant='p2' weight='medium'>
                    {t('submission.class-performance-label', 'Class Performance')}
                </Text>
                <FilterSection
                    options={filterOptions.criteria}
                    selectedId={filters.criterion}
                    onSelect={(id) =>
                        setFilters((prev) => ({
                            ...prev,
                            criterion: prev.criterion === id && id !== FilterOption.ALL ? FilterOption.ALL : id,
                        }))
                    }
                    labelKey='title'
                />
            </Stack>
            <MetricCardBox device={device}>
                <MetricCard label={t('submission.scores-metric.high', 'High Score')} value={`${stats.high}%`} />
                <MetricCard label={t('submission.scores-metric.low', 'Low Score')} value={`${stats.low}%`} />
                <MetricCard label={t('submission.scores-metric.median', 'Median Score')} value={`${stats.median}%`} />
                <MetricCard label={t('submission.scores-metric.mean', 'Mean Score')} value={`${stats.mean}%`} />
                <MetricCard
                    label={t('submission.scores-metric.ai-accuracy', 'AI Accuracy')}
                    value={`${stats.aiAccuracy}%`}
                />
            </MetricCardBox>
            <FilterSection
                label={t('submission.view-by-grader-label', 'View by Grader')}
                options={filterOptions.graders}
                selectedId={filters.grader}
                onSelect={onGraderFilterChange}
                labelKey='name'
            />
            <Stack spacing={5}>
                <Text variant='p2'>{chartTitle}</Text>
                <PerformanceChart chartData={chartData} filters={filters} filterOptions={filterOptions} />
            </Stack>
        </Container>
    );
};

const MetricCard = ({ label, value }) => (
    <MetricCardBase>
        <Stack spacing={3}>
            <Text variant='p2' color='light'>
                {label}
            </Text>
            <Text variant='p2' weight='medium'>
                {value}
            </Text>
        </Stack>
    </MetricCardBase>
);

const FilterSection = ({ label, options, selectedId, onSelect, labelKey }) => {
    const { ref, events, isDragging } = useDraggableScroll();

    return (
        <Stack>
            {label && <Text variant='p2'>{label}</Text>}
            <HorizontalScrollStack direction='row' ref={ref} {...events} $isDragging={isDragging}>
                {options.map((opt) => (
                    <StyledChip
                        key={opt.id}
                        variant='secondary'
                        color='neutral'
                        onClick={() => onSelect(opt.id)}
                        isSelected={Array.isArray(selectedId) ? selectedId.includes(opt.id) : selectedId === opt.id}
                        tooltip={{ component: opt[labelKey] }}
                    >
                        {opt[labelKey]}
                    </StyledChip>
                ))}
            </HorizontalScrollStack>
        </Stack>
    );
};

const HorizontalScrollStack = styled(Stack)(({ $isDragging }) => ({
    overflowX: 'auto',
    '&::-webkit-scrollbar': {
        display: 'none',
    },
    '-ms-overflow-style': 'none',
    'scrollbar-width': 'none',
    cursor: $isDragging ? 'grabbing' : 'grab',
    userSelect: 'none',
    paddingLeft: '0.25rem',

    '& > button:first-child': {
        marginLeft: '-0.25rem',
    },
}));

const Container = styled(Stack)(({ theme }) => ({
    backgroundColor: theme.palette.ensightful.shades[0],
    padding: '1.25rem',
    borderRadius: '12px',
}));

const MetricCardBox = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'device',
})(({ theme, device }) => ({
    display: 'grid',
    gap: theme.spacing(3),
    gridTemplateColumns: device === 'mobile' ? '1fr 1fr' : 'repeat(5, 1fr)',

    '& > *:last-child': {
        gridColumn: device === 'mobile' ? 'span 2' : 'span 1',
    },
}));

const MetricCardBase = styled(Paper)(({ theme }) => ({
    padding: '1rem',
    flex: 1,
    borderRadius: '16px',
    boxShadow: 'none',
    border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
}));

const StyledChip = styled(Button, {
    shouldForwardProp: (prop) => prop !== 'isSelected',
})(({ theme, isSelected }) => ({
    fontWeight: 'normal',
    maxWidth: '180px',
    padding: '0.625rem',
    margin: '0.5rem 0.2rem',

    '& > div.MuiBox-root': {
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
    },

    ...(isSelected && {
        borderColor: theme.palette.ensightful.primary[500],
        backgroundColor: theme.palette.ensightful.primary[50],
        color: theme.palette.ensightful.primary[700],

        '&:hover': {
            backgroundColor: theme.palette.ensightful.primary[100],
        },
    }),
}));

export default ClassPerformance;
